<script setup lang="ts">
import { onMounted, watch, ref, computed, nextTick } from 'vue';
import dayjs from 'dayjs';
import PractitionerPicker from './components/PractitionerPicker.vue';
import ServicePicker from './components/ServicePicker.vue';
import Calendar from './components/Calendar.vue';
import BookingForm from './components/BookingForm.vue';
import StepHeader from './components/StepHeader.vue';
import SuccessMessage from './components/SuccessMessage.vue';
import ErrorMessage from './components/ErrorMessage.vue';
import type { AvailabilityResponse, OrderPayload, PractitionerEntry, ServiceEntry, OrderResult } from './api';
import { fetchEntries, fetchAvailability, fetchServicesByUser, postOrder } from './api';
import type { BookingProvider } from './provider';
import type { VisitLanguageValue } from './visitLanguage';

type OverallMode = 'service' | 'practitioner' | 'mixed' | 'all';

const props = defineProps<{
  restUrl: string;
  mode: OverallMode | string;
  services: string[];
  users: string[];
  provider: BookingProvider;
  visitLanguage: VisitLanguageValue;
  visitLanguageRequired: boolean;
}>();

const showPrice = (price?: string) => {
  if (!price) {
    return false;
  }
  const numeric = Number(price);
  return Number.isFinite(numeric) && numeric > 0;
};

const services = ref<ServiceEntry[]>([]);
const practitioners = ref<PractitionerEntry[]>([]);
const selectedServiceUuid = ref<string | null>(null);
const overrideServiceUuid = ref<string | null>(null);
const finalServiceUuid = computed<string>(() => {
  return (overrideServiceUuid.value || selectedServiceUuid.value) || "";
});
const selectedPractitionerUuid = ref<string | null>(null);
const availability = ref<AvailabilityResponse | null>(null);

const currentMonth = ref<string>(dayjs().startOf('month').format('YYYY-MM-DD'));
const selectedDate = ref<string | null>(null);
const selectedSlot = ref<string | null>(null);

const loadingEntries = ref(false);
const loadingAvailability = ref(false);
const submitting = ref(false);
const successMessage = ref<boolean>(false);
const errorMessage = ref<string | null>(null);
const medfileErrorHtml = ref<string | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);

const SUCCESS_STORAGE_KEY = 'medfile_booking_success';
const SUCCESS_STORAGE_TTL_MS = 30 * 60 * 1000;

function persistBookingSuccess(): void {
  try {
    sessionStorage.setItem(
      SUCCESS_STORAGE_KEY,
      JSON.stringify({
        at: Date.now(),
        provider: props.provider,
      })
    );
  } catch {
    // sessionStorage may be unavailable (private mode / blocked).
  }
}

function clearBookingSuccess(): void {
  try {
    sessionStorage.removeItem(SUCCESS_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function restoreBookingSuccessFromSession(): boolean {
  try {
    const raw = sessionStorage.getItem(SUCCESS_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const data = JSON.parse(raw) as { at?: number; provider?: string };
    if (
      typeof data.at !== 'number' ||
      Date.now() - data.at > SUCCESS_STORAGE_TTL_MS ||
      (data.provider && data.provider !== props.provider)
    ) {
      clearBookingSuccess();
      return false;
    }
    return true;
  } catch {
    clearBookingSuccess();
    return false;
  }
}

const effectiveMode = ref<OverallMode>('mixed');

const availabilityAbortController = ref<AbortController | null>(null);
let availabilityDebounceTimeout: number | null = null;

const lastAvailabilityStartYmd = ref<string | null>(null);
const lastAvailabilityEndYmd = ref<string | null>(null);


const selectedService = computed<ServiceEntry | null>(() =>
  services.value.find((s) => s.uuid === selectedServiceUuid.value) || null
);

const selectedPractitioner = computed<PractitionerEntry | null>(() =>
  practitioners.value.find((p) => p.uuid === selectedPractitionerUuid.value) || null
);

function sameEntityId(a: unknown, b: unknown): boolean {
  if (a == null || b == null) {
    return false;
  }
  return String(a) === String(b);
}

function sortServicesWithDefaultFirst(
  list: ServiceEntry[],
  defaultUuid: string | null
): ServiceEntry[] {
  if (!defaultUuid || list.length <= 1) {
    return list;
  }
  const index = list.findIndex((s) => sameEntityId(s.uuid, defaultUuid));
  if (index < 0) {
    return list;
  }
  if (index === 0) {
    return list;
  }
  const sorted = [...list];
  const [first] = sorted.splice(index, 1);
  sorted.unshift(first);
  return sorted;
}

const servicesForPicker = computed(() => {
  const defaultUuid = resolveDefaultServiceUuid(
    selectedPractitioner.value,
    services.value
  );
  // Order by practitioner default only — selection must not reshuffle the list.
  return sortServicesWithDefaultFirst(services.value, defaultUuid);
});

const hasCompleteSelection = computed(
  () => !!finalServiceUuid.value && !!selectedPractitionerUuid.value
);

const calendarState = computed(() =>
  displayMode.value === 'form' ? 'DateChosen' : 'DateNotChosen'
);

function resolveDefaultServiceUuid(
  practitioner: PractitionerEntry | null | undefined,
  serviceList: ServiceEntry[]
): string | null {
  if (!serviceList.length) {
    return null;
  }
  const fromApi = practitioner?.defaultServiceUuid;
  if (fromApi != null && fromApi !== '' && serviceList.some((s) => sameEntityId(s.uuid, fromApi))) {
    return String(fromApi);
  }
  return serviceList[0]?.uuid != null ? String(serviceList[0].uuid) : null;
}

async function loadEntries() {
  const previousLoadingEntries = loadingEntries.value;
  loadingEntries.value = true;
  errorMessage.value = null;
  try {
    const data = await fetchEntries(props.restUrl, props.users, props.services, props.provider);
    effectiveMode.value = data.mode;

    const raw = data.entries;
    const usersRaw = Array.isArray(raw.users) ? raw.users : [];
    const servicesRaw = Array.isArray(raw.services) ? raw.services : [];

    services.value = servicesRaw;
    practitioners.value = usersRaw;

    if (effectiveMode.value === 'all' && usersRaw.length > 0) {
      selectedPractitionerUuid.value = usersRaw[0].uuid;
      selectedServiceUuid.value = resolveDefaultServiceUuid(usersRaw[0], servicesRaw);
    } else if (usersRaw.length === 1) {
      selectedPractitionerUuid.value = usersRaw[0].uuid;
      selectedServiceUuid.value = resolveDefaultServiceUuid(usersRaw[0], servicesRaw);
    } else {
      selectedPractitionerUuid.value = null;
      selectedServiceUuid.value =
        servicesRaw.length === 1 ? servicesRaw[0].uuid : null;
    }

    if (effectiveMode.value !== 'all' && servicesRaw.length === 1) {
      selectedServiceUuid.value = servicesRaw[0].uuid;
    }

    overrideServiceUuid.value = null;
    availability.value = null;
    selectedDate.value = null;
    selectedSlot.value = null;

    if (effectiveMode.value === 'all' && selectedPractitionerUuid.value && !selectedServiceUuid.value) {
      await loadServicesForPractitioner(selectedPractitionerUuid.value);
    } else if (hasCompleteSelection.value) {
      await nextTick();
      if (availabilityDebounceTimeout !== null) {
        clearTimeout(availabilityDebounceTimeout);
        availabilityDebounceTimeout = null;
      }
      await refreshAvailability();
    }
  } catch (err: any) {
    errorMessage.value = err?.message || 'Nie udało się pobrać danych.';
  } finally {
    loadingEntries.value = previousLoadingEntries;
  }
}

async function loadServicesForPractitioner(userUuid: string) {
  try {
    const data = await fetchServicesByUser(props.restUrl, userUuid, props.provider);
    const nextServices = Array.isArray(data.services) ? data.services : [];
    services.value = nextServices;

    const practitioner = practitioners.value.find((p) => p.uuid === userUuid);
    selectedServiceUuid.value = resolveDefaultServiceUuid(practitioner, nextServices);
    overrideServiceUuid.value = null;

    if (hasCompleteSelection.value) {
      await refreshAvailability();
    }
  } catch (err: any) {
    services.value = [];
    selectedServiceUuid.value = null;
    overrideServiceUuid.value = null;
    errorMessage.value = err?.message || 'Nie udało się pobrać usług.';
  }
}

async function refreshAvailability() {
  if (!hasCompleteSelection.value || !finalServiceUuid.value || !selectedPractitioner.value) {
    availability.value = null;
    loadingAvailability.value = false;
    return;
  }

  // Anuluj poprzednie zapytanie o dostępność, jeśli jeszcze trwa.
  if (availabilityAbortController.value) {
    availabilityAbortController.value.abort();
  }
  const controller = new AbortController();
  availabilityAbortController.value = controller;

  loadingAvailability.value = true;
  errorMessage.value = null;

  const base = dayjs(currentMonth.value || dayjs().startOf('month')).startOf('month');
  const today = dayjs().startOf('day');
  // Medchart: jeden zakres = dziś → +183 dni (limit API) — bez kolejnych refetchy przy zmianie miesiąca.
  // Medfile: ~3 miesiące jak wcześniej.
  const startYmd =
    props.provider === 'medchart'
      ? today.format('YYYYMMDD')
      : base.format('YYYYMMDD');
  const endYmd =
    props.provider === 'medchart'
      ? today.add(183, 'day').format('YYYYMMDD')
      : base.add(3, 'month').endOf('month').format('YYYYMMDD');
  currentMonth.value = base.format('YYYY-MM-DD');
  lastAvailabilityStartYmd.value = startYmd;
  lastAvailabilityEndYmd.value = endYmd;

  try {
    const data = await fetchAvailability(
      props.restUrl,
      selectedPractitioner.value.uuid,
      finalServiceUuid.value,
      startYmd,
      endYmd,
      props.provider,
      controller.signal
    );

    // Ignore stale response if a newer request already started.
    if (availabilityAbortController.value !== controller) {
      return;
    }

    availability.value = data;

    const slotsByDate = data.slots || {};
    const allDates = Object.keys(slotsByDate)
      .filter((d) => Array.isArray(slotsByDate[d]) && (slotsByDate[d] as string[]).length > 0)
      .sort();

    if (allDates.length) {
      const lastSlotYmd = allDates[allDates.length - 1].replace(/-/g, '');
      if (lastSlotYmd > endYmd) {
        lastAvailabilityEndYmd.value = lastSlotYmd;
      }
    }

    if (!allDates.length) {
      selectedDate.value = null;
      selectedSlot.value = null;
      return;
    }

    const monthPrefix = base.format('YYYY-MM');

    const currentHasSlots =
      selectedDate.value &&
      slotsByDate[selectedDate.value] &&
      (slotsByDate[selectedDate.value] as string[]).length > 0 &&
      (selectedDate.value as string).startsWith(monthPrefix);

    if (!currentHasSlots) {
      const firstInCurrentMonth =
        allDates.find((d) => d.startsWith(monthPrefix)) || null;

      if (firstInCurrentMonth) {
        selectedDate.value = firstInCurrentMonth;
        selectedSlot.value = null;
      } else {
        // W bieżącym miesiącu nie ma terminów – bierzemy pierwszy możliwy
        // termin z kolejnych miesięcy i przestawiamy aktywny miesiąc.
        const firstOverall = allDates[0] || null;

        if (firstOverall) {
          selectedDate.value = firstOverall;
          selectedSlot.value = null;
          currentMonth.value = dayjs(firstOverall).startOf('month').format('YYYY-MM-DD');
        } else {
          selectedDate.value = null;
          selectedSlot.value = null;
        }
      }
    }
  } catch (err: any) {
    // Jeśli żądanie zostało przerwane przez nowsze, nie gasimy loading.
    if (err && err.name === 'AbortError') {
      return;
    }
    if (availabilityAbortController.value !== controller) {
      return;
    }
    availability.value = null;
    selectedDate.value = null;
    selectedSlot.value = null;
    errorMessage.value = err?.message || 'Nie udało się pobrać dostępności.';
  } finally {
    if (availabilityAbortController.value === controller) {
      loadingAvailability.value = false;
    }
  }
}

function scheduleRefreshAvailability() {
  // Show loading immediately so the calendar never flickers to an empty state
  // during debounce / abort of the previous request.
  if (hasCompleteSelection.value) {
    loadingAvailability.value = true;
  }

  if (availabilityDebounceTimeout !== null) {
    clearTimeout(availabilityDebounceTimeout);
  }
  availabilityDebounceTimeout = window.setTimeout(() => {
    refreshAvailability();
  }, 250);
}

function handleServiceSelect(serviceUuid: string | null, isOverride: boolean = false) {
  if(!isOverride) {
    selectedServiceUuid.value = serviceUuid;
    overrideServiceUuid.value = null;
  } else {
    overrideServiceUuid.value = serviceUuid;
  }
}

function handleMonthChange(direction: 'prev' | 'next') {
  const base = dayjs(currentMonth.value || dayjs().startOf('month'));
  const currentCalendarMonth = dayjs().startOf('month');
  const next =
    direction === 'prev'
      ? base.subtract(1, 'month').startOf('month')
      : base.add(1, 'month').startOf('month');

  // Do not navigate to months before the current month.
  if (direction === 'prev' && next.isBefore(currentCalendarMonth, 'month')) {
    return;
  }

  const monthStart = next.startOf('month');
  const monthEnd = monthStart.endOf('month');
  const monthStartYmd = monthStart.format('YYYYMMDD');
  const monthEndYmd = monthEnd.format('YYYYMMDD');

  currentMonth.value = monthStart.format('YYYY-MM-DD');

  const start = lastAvailabilityStartYmd.value;
  const end = lastAvailabilityEndYmd.value;

  // Refresh only when the requested month does not overlap the loaded range.
  // This matters for the current Medchart month, whose loaded range starts today.
  if (!start || !end || monthEndYmd < start || monthStartYmd > end) {
    selectedDate.value = null;
    selectedSlot.value = null;
    scheduleRefreshAvailability();
  }
}

function handleDateSelect(date: string) {
  selectedDate.value = date;
  selectedSlot.value = null;
}

function handleTimeSelect(datetime: string) {
  selectedSlot.value = datetime;
}

async function handleFormSubmit(payload: {
  name: string;
  lastname: string;
  phone: string;
  email: string;
  birth_date?: string;
  visit_language?: VisitLanguageValue;
}) {
  if (!hasCompleteSelection.value || !selectedService.value || !selectedPractitioner.value || !selectedSlot.value) {
    return;
  }
  submitting.value = true;
  errorMessage.value = null;
  successMessage.value = false;

  const order: OrderPayload = {
    chosen_user: selectedPractitioner.value.uuid,
    chosen_service: finalServiceUuid.value,
    chosen_date: selectedSlot.value,
    email: payload.email,
    phone: payload.phone,
    patient_name: payload.name,
    patient_lastname: payload.lastname,
    page_url: window.location.href,
    user_name: `${selectedPractitioner.value.name} ${selectedPractitioner.value.lastname}`.trim(),
    service_name: selectedService.value?.name ?? '',
  };

  if (props.provider === 'medchart') {
    order.birth_date = payload.birth_date;
    order.visit_language = payload.visit_language ?? '';
  }

  const result = await postOrder(props.restUrl, order, props.provider);
  submitting.value = false;

  if (!result.success) {
    if ((result.medfileError || result.providerError) && result.errorHtml) {
      medfileErrorHtml.value = result.errorHtml;
      await scrollToWrapper();
      return;
    }
    errorMessage.value = result.message || 'Nie udało się zapisać. Spróbuj ponownie.';
    return;
  }

  persistBookingSuccess();
  successMessage.value = true;
  await scrollToWrapper();
}

async function scrollToWrapper() {
  await nextTick();
  wrapperRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// Reagujemy na zmianę miesiąca / serwisu / terapeuty jednym miejscem.
watch(
  [finalServiceUuid, selectedPractitionerUuid],
  () => {
    if (loadingEntries.value) {
      return;
    }
    selectedDate.value = null;
    selectedSlot.value = null;
    scheduleRefreshAvailability();
  }
);

// Przy zmianie fizjoterapeuty resetujemy miesiąc do bieżącego,
// aby kalendarz pokazywał miesiąc z najbliższym dostępnym terminem
// dla nowej osoby, a nie ten odziedziczony po poprzednim wyborze.
watch(
  selectedPractitionerUuid,
  () => {
    if (loadingEntries.value) {
      return;
    }
    currentMonth.value = dayjs().startOf('month').format('YYYY-MM-DD');

    if (effectiveMode.value === 'all' && selectedPractitionerUuid.value) {
      selectedDate.value = null;
      selectedSlot.value = null;
      availability.value = null;
      loadServicesForPractitioner(selectedPractitionerUuid.value);
    } else if (effectiveMode.value === 'mixed' && selectedPractitionerUuid.value) {
      selectedDate.value = null;
      selectedSlot.value = null;
      availability.value = null;
      const practitioner = practitioners.value.find(
        (p) => p.uuid === selectedPractitionerUuid.value
      );
      selectedServiceUuid.value = resolveDefaultServiceUuid(practitioner, services.value);
      overrideServiceUuid.value = null;
    }
  }
);

const fieldsOrder = computed(() => {
  if (effectiveMode.value === 'practitioner' || effectiveMode.value === 'all') {
    return ['practitioner', 'service'];
  }

  return ['service', 'practitioner'];
});

const displayMode = ref<'configuration'|'form'>('configuration');

const practitionerMode = computed(() => {
  if (effectiveMode.value === 'practitioner' || practitioners.value.length === 1) {
    return 'PractitionerMode';
  }
  return displayMode.value === 'form' ? 'PractitionerChosen' : 'PractitionerNotChosen';
});

const serviceMode = computed(() => {
  if (
    effectiveMode.value !== 'all' &&
    practitionerMode.value !== 'PractitionerMode' &&
    (effectiveMode.value === 'service' || services.value.length === 1)
  ) {
    return 'ServiceMode';
  }
  return displayMode.value === 'form' ? 'ServiceChosen' : 'ServiceNotChosen';
});



const toggleDisplayMode = () => {
  displayMode.value = displayMode.value === 'configuration' ? 'form' : 'configuration';
};

onMounted(() => {
  if (restoreBookingSuccessFromSession()) {
    successMessage.value = true;
  }
  loadEntries();
});

function reset() {
  displayMode.value = 'configuration';
  errorMessage.value = null;
  medfileErrorHtml.value = null;
  successMessage.value = false;
  clearBookingSuccess();
  selectedDate.value = null;
  selectedSlot.value = null;
}
</script>

<template>
  <div class="mbw-wrapper" ref="wrapperRef">
    <div v-if="errorMessage" class="mbw-error">
      {{ errorMessage }}
    </div>

    <div v-if="loadingEntries" class="mbw-empty">
      Ładowanie danych...
    </div>

    <template v-else-if="medfileErrorHtml">
      <ErrorMessage :html="medfileErrorHtml" />

      <button class="mbw-text-link" @click="reset" type="button" style="display: block; margin: 0 auto;">Wróć do formularza</button>
    </template>

    <template v-else-if="successMessage">
      <SuccessMessage :provider="provider" />
    </template>

    <template v-else>

      <h2 class="mbw-heading">Umów się na wizytę</h2>
      <template v-for="(field, index) in fieldsOrder" :key="index">
        <StepHeader
          v-if="field === 'practitioner' && practitionerMode !== 'PractitionerMode'"
          :step-number="index"
          :label="practitionerMode === 'PractitionerChosen' ? 'Specjalista' : 'Wybierz specjalistę'"
          :show-checkmark="practitionerMode === 'PractitionerChosen' || !!selectedPractitionerUuid"
        />
        <PractitionerPicker
          v-if="field === 'practitioner'"
          :state="practitionerMode"
          :items="practitioners"
          :services="services"
          :model-value="selectedPractitionerUuid"
          :selected-date="selectedDate"
          @update:model-value="(val) => (selectedPractitionerUuid = val)"
          @update:service-value="(val) => (handleServiceSelect(val, true))"
          :displayPrice="effectiveMode === 'service'"
          :showPrice="showPrice"
          @toggle-display-mode="toggleDisplayMode"
        />
        <StepHeader
          v-if="field === 'service' && serviceMode !== 'ServiceMode'"
          :step-number="index"
          :label="serviceMode === 'ServiceChosen' ? 'Usługa' : 'Wybierz usługę'"
          :show-checkmark="serviceMode === 'ServiceChosen' || !!selectedServiceUuid"
        />
        <ServicePicker
          v-if="field === 'service'"
          :state="serviceMode"
          :items="servicesForPicker"
          :model-value="selectedServiceUuid"
          :selected-date="selectedDate"
          @update:model-value="handleServiceSelect"
          :displayPrice="effectiveMode !== 'service'"
          :showPrice="showPrice"
          @toggle-display-mode="toggleDisplayMode"
        />
      </template>

      <StepHeader
        :step-number="fieldsOrder.length"
        :label="calendarState === 'DateChosen' ? 'Termin' : 'Wybierz termin'"
        :show-checkmark="calendarState === 'DateChosen' || (!!selectedDate && !!selectedSlot)"
        :class="{ 'mbw-future-step': !hasCompleteSelection}"
      />
      <Calendar
        :state="calendarState"
        :availability="availability"
        :current-date="currentMonth"
        :selected-date="selectedDate"
        :selected-slot="selectedSlot"
        :enabled="hasCompleteSelection"
        :loading-availability="loadingAvailability"
        @change-month="handleMonthChange"
        @select-date="handleDateSelect"
        @select-slot="handleTimeSelect"
        @toggle-display-mode="toggleDisplayMode"
      />

      <StepHeader
        :step-number="fieldsOrder.length + 1"
        label="Podaj dane"
        :class="{ 'mbw-future-step': displayMode === 'configuration'}"
      />
      <div class="mbw-form-wrapper" v-if="displayMode === 'form'">
        <BookingForm
          :state="selectedSlot ? 'FormNotFilled' : 'FormFilled'"
          :selected-service-uuid="selectedServiceUuid"
          :selected-practitioner-uuid="selectedPractitionerUuid"
          :selected-date="selectedDate"
          :datetime="selectedSlot"
          :submitting="submitting"
          :provider="provider"
          :visit-language="visitLanguage"
          :visit-language-required="visitLanguageRequired"
          @submit="handleFormSubmit"
        />
      </div>
    </template>
  </div>
</template>

